import { useEffect, useMemo, useState } from 'react'
import { Clock, RotateCcw, Zap } from 'lucide-react'
import {
  clearTimeOverride,
  getTimeOverrideOffsetSeconds,
  onTimeOverrideChange,
  realNowMs,
  setTimeOverrideOffsetSeconds,
  shiftedNow,
} from '@/lib/timeOverride'

const SEC = 1
const MIN = 60 * SEC
const HOUR = 60 * 60
const DAY = 24 * HOUR
const WEEK = 7 * DAY

const PRESETS: Array<{ label: string; seconds: number }> = [
  { label: '−30d', seconds: -30 * DAY },
  { label: '−7d', seconds: -7 * DAY },
  { label: '−1d', seconds: -1 * DAY },
  { label: '−1h', seconds: -1 * HOUR },
  { label: '−5m', seconds: -5 * MIN },
  { label: '+5m', seconds: 5 * MIN },
  { label: '+1h', seconds: 1 * HOUR },
  { label: '+1d', seconds: 1 * DAY },
  { label: '+7d', seconds: 7 * DAY },
  { label: '+30d', seconds: 30 * DAY },
]

function formatLocal(d: Date): string {
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function toDateTimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

function humanDuration(totalSeconds: number): string {
  if (totalSeconds === 0) return 'no shift'
  const abs = Math.abs(totalSeconds)
  const sign = totalSeconds > 0 ? '+' : '−'
  const parts: string[] = []
  const w = Math.floor(abs / WEEK)
  const d = Math.floor((abs % WEEK) / DAY)
  const h = Math.floor((abs % DAY) / HOUR)
  const m = Math.floor((abs % HOUR) / MIN)
  const s = Math.floor(abs % MIN)
  if (w) parts.push(`${w}w`)
  if (d) parts.push(`${d}d`)
  if (h) parts.push(`${h}h`)
  if (m) parts.push(`${m}m`)
  if (s && !w && !d) parts.push(`${s}s`)
  return `${sign}${parts.join(' ')}`
}

interface Props {
  variant?: 'modal' | 'page'
}

export const TimeOverridePanel = ({ variant = 'modal' }: Props) => {
  const [offset, setOffset] = useState(() => getTimeOverrideOffsetSeconds())
  const [tick, setTick] = useState(0)
  const [datePickerValue, setDatePickerValue] = useState(() =>
    toDateTimeLocalValue(shiftedNow())
  )

  // Single subscription to override changes. Re-syncs both the offset and the
  // picker's starting value in one go (event-driven, not effect-driven), so
  // the field gets a sensible new seed after every quick-jump / external
  // change but never gets clobbered every second by the live-clock ticker.
  useEffect(
    () =>
      onTimeOverrideChange(() => {
        const next = getTimeOverrideOffsetSeconds()
        setOffset(next)
        setDatePickerValue(toDateTimeLocalValue(new Date(realNowMs() + next * 1000)))
      }),
    []
  )

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  const realDate = useMemo(() => new Date(realNowMs()), [tick])
  const shiftedDate = useMemo(() => new Date(realNowMs() + offset * 1000), [offset, tick])

  const adjust = (delta: number) => setTimeOverrideOffsetSeconds(offset + delta)

  const setAbsolute = () => {
    const target = new Date(datePickerValue)
    if (Number.isNaN(target.getTime())) return
    const newOffset = Math.round((target.getTime() - realNowMs()) / 1000)
    setTimeOverrideOffsetSeconds(newOffset)
  }

  const isShifted = offset !== 0

  return (
    <div className={variant === 'page' ? 'space-y-5' : 'space-y-4 px-6 py-5'}>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <div className="mb-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            <Clock size={11} /> Real time
          </div>
          <div className="font-mono text-xs text-slate-700">{formatLocal(realDate)}</div>
        </div>
        <div
          className={`rounded-lg border px-3 py-2.5 ${
            isShifted ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'
          }`}
        >
          <div className="mb-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
            <Zap size={11} /> Shifted time
          </div>
          <div
            className={`font-mono text-xs ${
              isShifted ? 'font-bold text-amber-800' : 'text-slate-700'
            }`}
          >
            {formatLocal(shiftedDate)}
          </div>
        </div>
      </div>

      {isShifted && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
          Offset: <span className="font-mono font-bold">{humanDuration(offset)}</span> ({offset.toLocaleString()} s).
          Every API request now carries this header until you reset.
        </div>
      )}

      <div>
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Quick jumps (additive)
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => adjust(p.seconds)}
              className="cursor-pointer rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] font-bold text-slate-700 transition hover:border-[#3C3295] hover:text-[#3C3295]"
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={() => adjust(-1)}
            className="cursor-pointer rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] font-bold text-slate-700 transition hover:border-[#3C3295] hover:text-[#3C3295]"
          >
            −1s
          </button>
          <button
            type="button"
            onClick={() => adjust(1)}
            className="cursor-pointer rounded-md border border-slate-200 bg-white px-2 py-1.5 font-mono text-[11px] font-bold text-slate-700 transition hover:border-[#3C3295] hover:text-[#3C3295]"
          >
            +1s
          </button>
        </div>
      </div>

      <div>
        <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
          Jump to a specific time
        </div>
        <div className="flex gap-2">
          <input
            type="datetime-local"
            step="1"
            value={datePickerValue}
            onChange={(e) => setDatePickerValue(e.target.value)}
            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#3C3295] focus:ring-2 focus:ring-[#3C3295]/15"
          />
          <button
            type="button"
            onClick={setAbsolute}
            className="cursor-pointer rounded-lg bg-[#3C3295] px-4 py-2 text-xs font-bold text-white transition hover:bg-[#2A2266]"
          >
            Jump
          </button>
        </div>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[11px] text-slate-600 leading-relaxed">
        <div className="mb-1 font-bold text-slate-700">What this affects</div>
        <ul className="space-y-0.5 pl-4 list-disc">
          <li>Server: every <span className="font-mono">now()</span> / <span className="font-mono">Carbon::now()</span> in this request</li>
          <li>Client: UI components that call <span className="font-mono">shiftedNow()</span> from this lib</li>
        </ul>
        <div className="mt-1.5 mb-0.5 font-bold text-slate-700">What it does NOT affect</div>
        <ul className="space-y-0.5 pl-4 list-disc">
          <li>SQL <span className="font-mono">CURRENT_TIMESTAMP</span> defaults, cron jobs, external services</li>
          <li>Token / session expiry (left on real clock)</li>
          <li>Browser-native <span className="font-mono">Date.now()</span> in third-party libs</li>
        </ul>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={clearTimeOverride}
          disabled={!isShifted}
          className="inline-flex items-center gap-1.5 cursor-pointer rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <RotateCcw size={13} />
          Reset to real time
        </button>
      </div>
    </div>
  )
}
